import {useCallback, useEffect, useRef, useState} from 'react'

// Fixed reconnect interval. Simple and predictable — at 30 connected clients
// the load from a server blip is negligible.
const RECONNECT_INTERVAL_MS = 2000;
// App-level heartbeat to detect half-open sockets that look OPEN to the JS
// layer but are actually dead (network swap, brief signal loss, NAT timeout
// in the middle, iOS PWA suspend/wake). Tight so detection is fast.
const HEARTBEAT_INTERVAL_MS = 5000;
const HEARTBEAT_TIMEOUT_MS = 2000;

const useSocket = ({token, clockDrift}) => {
  // socket state drives sendScene/sendRoundEnd via re-render, but we ALSO
  // need a ref so internal handlers (onclose, visibilitychange) always see
  // the latest live socket without React's stale-closure problem.
  const [socket, setSocket] = useState();
  const socketRef = useRef(null);
  const [scene, setScene] = useState(0);
  const [roundEnd, setRoundEnd] = useState(0);
  const subscribersRef = useRef(new Set());

  // Track whether the current useSocket instance is still mounted, plus
  // any pending reconnect timer.
  const mountedRef = useRef(true);
  const reconnectTimerRef = useRef(null);
  // Ref the latest newSocket so the reconnect timer always calls the current
  // closure (which has the up-to-date token), not the first render's stale
  // version that captured token=undefined before /users/current resolved.
  const newSocketRef = useRef(null);
  // Heartbeat timers per active socket. Cleared on close/reconnect.
  const heartbeatIntervalRef = useRef(null);
  const heartbeatTimeoutRef = useRef(null);
  // Ref the latest forceReconnect so sendOrReconnect (defined inline before
  // forceReconnect's useCallback) can call it without a circular dep.
  const forceReconnectRef = useRef(null);

  // sendOrReconnect: try to send; if the socket isn't OPEN, kick a reconnect
  // immediately so the next user action lands on a healthy connection.
  // (We don't queue/replay the missed message — the room can recover via
  // the next state push, and naive replay risks duplicate scene transitions.)
  const sendOrReconnect = useCallback((payload) => {
    const s = socketRef.current;
    if (s && s.readyState === WebSocket.OPEN) {
      try { s.send(JSON.stringify(payload)); return; } catch (e) {}
    }
    // A connection attempt is already in flight — let it finish. Forcing a
    // reconnect on a CONNECTING socket kills the handshake, triggers a
    // re-render, which fires the send effect again, which kills the next
    // handshake... an infinite connect/kill loop ("Maximum update depth
    // exceeded"). The message is intentionally dropped; the next state push
    // recovers.
    if (s && s.readyState === WebSocket.CONNECTING) return;
    if (reconnectTimerRef.current) return; // reconnect already scheduled
    // Fall through: socket is genuinely dead — force reconnect now.
    if (forceReconnectRef.current) forceReconnectRef.current('send on dead socket');
  }, []);

  // Stable identities: these land in consumers' useEffect dependency arrays,
  // so recreating them per render re-fires those effects on every render.
  const sendScene = useCallback((sceneNumber) => {
    sendOrReconnect({scene: sceneNumber, timestamp: Date.now() + clockDrift});
  }, [sendOrReconnect, clockDrift]);
  const sendRoundEnd = useCallback((roundEnd) => {
    sendOrReconnect({roundEnd});
  }, [sendOrReconnect]);

  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current) return;
    if (reconnectTimerRef.current) return; // already scheduled
    console.log(`[ws] scheduling reconnect in ${RECONNECT_INTERVAL_MS}ms`);
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      // Read the latest newSocket via ref to avoid the first-render stale
      // closure (where token was still undefined).
      const fn = newSocketRef.current;
      if (fn) fn();
    }, RECONNECT_INTERVAL_MS);
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
  }, []);

  // Force-close the current socket and reconnect IMMEDIATELY (no scheduled
  // delay). Used when the heartbeat detects a dead socket, when a send hits
  // a non-OPEN state, or when foreground/online events fire.
  const forceReconnect = useCallback((reason) => {
    console.log(`[ws] forcing reconnect: ${reason}`);
    stopHeartbeat();
    const s = socketRef.current;
    if (s) {
      s.onclose = null;
      s.onerror = null;
      try { s.close(); } catch (e) {}
      socketRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    const fn = newSocketRef.current;
    if (fn) fn();
  }, [stopHeartbeat]);
  forceReconnectRef.current = forceReconnect;

  const sendPing = useCallback(() => {
    const s = socketRef.current;
    if (!s || s.readyState !== WebSocket.OPEN) return;
    if (heartbeatTimeoutRef.current) return; // already waiting for a pong
    try { s.send(JSON.stringify({ping: Date.now()})); } catch (e) { return; }
    heartbeatTimeoutRef.current = setTimeout(() => {
      heartbeatTimeoutRef.current = null;
      forceReconnect('heartbeat timeout');
    }, HEARTBEAT_TIMEOUT_MS);
  }, [forceReconnect]);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatIntervalRef.current = setInterval(sendPing, HEARTBEAT_INTERVAL_MS);
  }, [sendPing, stopHeartbeat]);

  const newSocket = useCallback(() => {
    if (!token) return;
    if (!mountedRef.current) return;

    // Tear down any existing socket first so we don't leak.
    const existing = socketRef.current;
    if (existing && existing.readyState !== WebSocket.CLOSED) {
      existing.onclose = null; // suppress reconnect on intentional close
      existing.onerror = null;
      try { existing.close(); } catch (e) {}
    }

    const s = new WebSocket((window.location.protocol === "https:" ? "wss://" : "ws://") + window.location.host + "?token=" + token);
    socketRef.current = s;

    s.onopen = () => {
      console.log('[ws] open');
      startHeartbeat();
    };

    s.onmessage = (message) => {
      const data = JSON.parse(message.data);
      // Pong from server clears the in-flight heartbeat timeout — connection
      // is alive end-to-end. (Any inbound message could in principle prove
      // liveness, but pong is unambiguous and cheap to special-case.)
      if (data.pong !== undefined) {
        if (heartbeatTimeoutRef.current) {
          clearTimeout(heartbeatTimeoutRef.current);
          heartbeatTimeoutRef.current = null;
        }
        return;
      }
      if (data.scene !== undefined) {
        setScene(data);
      } else if (data.roundEnd !== undefined) {
        setRoundEnd(data.roundEnd);
      } else {
        subscribersRef.current.forEach(callback => callback(data));
      }
    };

    s.onclose = (event) => {
      console.log(`[ws] close code=${event.code} reason=${event.reason || '(none)'}`);
      stopHeartbeat();
      if (socketRef.current === s) socketRef.current = null;
      scheduleReconnect();
    };

    s.onerror = (event) => {
      console.log('[ws] error', event);
      // onclose will fire after onerror; reconnect happens there.
    };

    setSocket(s);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, scheduleReconnect, startHeartbeat, stopHeartbeat]);

  // Keep the ref pointed at the latest newSocket so the reconnect timer
  // (closed over by scheduleReconnect on first render) always invokes the
  // up-to-date version with the current token.
  newSocketRef.current = newSocket;

  // Connect on token availability; tear down on unmount.
  useEffect(() => {
    mountedRef.current = true;
    newSocket();
    return () => {
      mountedRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      const s = socketRef.current;
      if (s) {
        s.onclose = null;
        s.onerror = null;
        try { s.close(); } catch (e) {}
        socketRef.current = null;
      }
    };
  }, [token, newSocket]);

  // Always force a fresh connection on any signal that the app might have
  // missed events: visibility change, BFCache restore, focus, network back
  // online. Cheaper to just reopen than to introspect a possibly-dead socket
  // — and we already debounce naturally because forceReconnect tears down
  // any in-flight reconnect.
  //
  // PWA-specific safety net: in iOS standalone mode the JS context can stay
  // paused longer than a regular tab and the freshly-created WebSocket can
  // fail to connect because the network stack hasn't fully woken. If the
  // reconnect doesn't establish within PWA_RECONNECT_GRACE_MS, hard-reload
  // the page so the user gets a fully fresh app instead of a stuck shell.
  useEffect(() => {
    const isStandalone =
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      window.navigator.standalone === true;
    const PWA_RECONNECT_GRACE_MS = 2500;
    let pwaReloadTimer = null;

    const armPwaFallback = () => {
      if (!isStandalone) return;
      if (pwaReloadTimer) clearTimeout(pwaReloadTimer);
      pwaReloadTimer = setTimeout(() => {
        const s = socketRef.current;
        if (!s || s.readyState !== WebSocket.OPEN) {
          console.log('[ws] PWA reconnect grace expired, reloading page');
          try { window.location.reload(); } catch (e) {}
        }
        pwaReloadTimer = null;
      }, PWA_RECONNECT_GRACE_MS);
    };

    const onVisibilityChange = () => {
      if (document.hidden) return;
      forceReconnect('visibility → visible');
      armPwaFallback();
    };
    const onPageShow = (e) => {
      forceReconnect(`pageshow persisted=${!!e.persisted}`);
      armPwaFallback();
    };
    const onFocus = () => {
      forceReconnect('window focus');
      armPwaFallback();
    };
    const onOnline = () => {
      forceReconnect('network online');
      armPwaFallback();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      if (pwaReloadTimer) clearTimeout(pwaReloadTimer);
    };
  }, [forceReconnect]);

  const receiveEvents = useCallback((callback) => {
    subscribersRef.current.add(callback);
    return () => subscribersRef.current.delete(callback);
  }, []);

  return [scene, sendScene, roundEnd, sendRoundEnd, receiveEvents];
}

export default useSocket
